import { get as lodashGet } from 'lodash';

import { createActions, handleActions } from 'redux-actions';

import constants from 'src/constants';
import selectors from './selectors';

const { types: errorModalTypes } = constants.errorModals;

const initialState = {
  isIosWebView: lodashGet(
    window,
    'webkit.messageHandlers.jsbridge.postMessage',
    false,
  ),
  isReactNativeWebView: lodashGet(
    window,
    'window.ReactNativeWebView.postMessage',
    false,
  ),
  activeEditingAttribute: {},
  activeEditingStyle: {},
  activeMode: null,
  breadcrumbData: {},
  errorModalType: null,
  initialized: false,
  isProductCatalogInitialized: false,
  isScreenshotModeActive: false,
  isSelectToolActive: false,
  jobDetails: {},
  macheteProducts: {},
  numberOfColorSelections: 0,
  nodeStyles: [],
  segmentsData: null,
  shareToken: null,
  variationGroups: {},
  variations: {},
};

const ACTIVATE_ERROR_MODAL = 'DESIGN_PRO/APP/ACTIVATE_ERROR_MODAL';
const DEACTIVATE_ERROR_MODAL = 'DESIGN_PRO/APP/DEACTIVATE_ERROR_MODAL';
const ADD_SPLIT_NODE_STYLES = 'DESIGN_PRO/APP/ADD_SPLIT_NODE_STYLES';
const DELETE_SPLIT_NODE_STYLES = 'DESIGN_PRO/APP/DELETE_SPLIT_NODE_STYLES';
const INCREMENT_COLOR_SELECTION_COUNT =
  'DESIGN_PRO/APP/INCREMENT_COLOR_SELECTION_COUNT';
const RECEIVE_MACHETE_DATA = 'DESIGN_PRO/APP/RECEIVE_MACHETE_DATA';
const RECEIVE_RENDERINGS = 'DESIGN_PRO/APP/RECEIVE_RENDERINGS';
const REPLACE_NODE_STYLES = 'DESIGN_PRO/APP/REPLACE_NODE_STYLES';
const SELECT_ELEMENTS_BY_ATTRIBUTE =
  'DESIGN_PRO/APP/SELECT_ELEMENTS_BY_ATTRIBUTE';
const SET_ACTIVE_EDITING_STYLE = 'DESIGN_PRO/APP/SET_ACTIVE_EDITING_STYLE';
const SET_ACTIVE_MODE = 'DESIGN_PRO/APP/SET_ACTIVE_MODE';
const SET_BREADCRUMB_DATA = 'DESIGN_PRO/APP/SET_BREADCRUMB_DATA';
const SET_DESIGN_PRO_INIT_COMPLETE =
  'DESIGN_PRO/APP/SET_DESIGN_PRO_INIT_COMPLETE';
const SET_PRODUCT_CATALOG_INITIALIZED =
  'DESIGN_PRO/APP/SET_PRODUCT_CATALOG_INITIALIZED';
const SET_IS_SCREENSHOT_MODE_ACTIVE =
  'DESIGN_PRO/APP/SET_IS_SCREENSHOT_MODE_ACTIVE';
const SET_IS_SELECT_TOOL_ACTIVE = 'DESIGN_PRO/APP/SET_IS_SELECT_TOOL_ACTIVE';
const SET_JOB_DETAILS = 'DESIGN_PRO/APP/SET_JOB_DETAILS';
const SET_NODE_STYLES = 'DESIGN_PRO/APP/SET_NODE_STYLES';
const SET_SEGMENTS_DATA = 'DESIGN_PRO/APP/SET_SEGMENTS_DATA';
const SET_SHARE_TOKEN = 'DESIGN_PRO/APP/SET_SHARE_TOKEN';

const actions = createActions({
  [ACTIVATE_ERROR_MODAL]: (type = errorModalTypes.DEFAULT) => ({ type }),
  [DEACTIVATE_ERROR_MODAL]: () => null,
  [ADD_SPLIT_NODE_STYLES]: (nodes, styleAttributes) => ({
    nodes,
    styleAttributes,
  }),
  [DELETE_SPLIT_NODE_STYLES]: (nodes) => ({ nodes }),
  [INCREMENT_COLOR_SELECTION_COUNT]: () => null,
  [RECEIVE_MACHETE_DATA]: (macheteData) => ({ macheteData }),
  [RECEIVE_RENDERINGS]: (renderings) => ({ renderings }),
  [REPLACE_NODE_STYLES]: (nodes, newStyle, updatedProps) => ({
    nodes,
    newStyle,
    updatedProps,
  }),
  [SELECT_ELEMENTS_BY_ATTRIBUTE]: (filterObject) => ({ filterObject }),
  [SET_ACTIVE_EDITING_STYLE]: (style, attribute) => ({ style, attribute }),
  [SET_ACTIVE_MODE]: (modeName) => ({ modeName }),
  [SET_BREADCRUMB_DATA]: (breadcrumbData) => ({ breadcrumbData }),
  [SET_DESIGN_PRO_INIT_COMPLETE]: () => null,
  [SET_PRODUCT_CATALOG_INITIALIZED]: () => null,
  [SET_IS_SELECT_TOOL_ACTIVE]: (isActive) => ({ isActive }),
  [SET_IS_SCREENSHOT_MODE_ACTIVE]: (isActive) => ({ isActive }),
  [SET_JOB_DETAILS]: (jobDetails) => ({ jobDetails }),
  [SET_NODE_STYLES]: (nodeStyles) => ({ nodeStyles }),
  [SET_SEGMENTS_DATA]: (segmentsData) => ({ segmentsData }),
  [SET_SHARE_TOKEN]: (shareToken) => ({ shareToken }),
});

const reducer = handleActions(
  {
    [actions.designPro.app.activateErrorModal]: (state, action) => ({
      ...state,
      errorModalType: action.payload.type,
    }),
    [actions.designPro.app.deactivateErrorModal]: (state) => ({
      ...state,
      errorModalType: null,
    }),
    [actions.designPro.app.deleteSplitNodeStyles]: (state, action) => ({
      ...state,
      nodeStyles: state.nodeStyles.filter(
        ({ uuid }) => !action.payload.nodes.includes(uuid),
      ),
    }),
    [actions.designPro.app.incrementColorSelectionCount]: (state) => ({
      ...state,
      numberOfColorSelections: state.numberOfColorSelections + 1,
    }),
    [actions.designPro.app.receiveMacheteData]: (state, action) => ({
      ...state,
      macheteProducts: action.payload.macheteData.products,
      variations: action.payload.macheteData.variations,
      variationGroups: action.payload.macheteData.variationGroups,
    }),
    [actions.designPro.app.receiveRenderings]: (state, action) => ({
      ...state,
      renderings: action.payload.renderings,
    }),
    [actions.designPro.app.setActiveEditingStyle]: (state, action) => ({
      ...state,
      activeEditingStyle: action.payload.style,
      activeEditingAttribute: action.payload.attribute,
    }),
    [actions.designPro.app.setActiveMode]: (state, action) => ({
      ...state,
      activeMode: action.payload.modeName,
    }),
    [actions.designPro.app.setDesignProInitComplete]: (state) => ({
      ...state,
      initialized: true,
    }),
    [actions.designPro.app.setProductCatalogInitialized]: (state) => ({
      ...state,
      isProductCatalogInitialized: true,
    }),
    [actions.designPro.app.setIsScreenshotModeActive]: (state, action) => ({
      ...state,
      isScreenshotModeActive: action.payload.isActive,
    }),
    [actions.designPro.app.setIsSelectToolActive]: (state, action) => ({
      ...state,
      isSelectToolActive: action.payload.isActive,
    }),
    [actions.designPro.app.setJobDetails]: (state, action) => ({
      ...state,
      jobDetails: action.payload.jobDetails,
    }),
    [actions.designPro.app.setNodeStyles]: (state, action) => ({
      ...state,
      nodeStyles: action.payload.nodeStyles,
    }),
    [actions.designPro.app.setSegmentsData]: (state, action) => ({
      ...state,
      segmentsData: action.payload.segmentsData,
    }),
    [actions.designPro.app.setBreadcrumbData]: (state, action) => ({
      ...state,
      breadcrumbData: action.payload.breadcrumbData,
    }),
    [actions.designPro.app.setShareToken]: (state, action) => ({
      ...state,
      shareToken: action.payload.shareToken,
    }),
  },
  initialState,
);

export default {
  initialState,
  actions: actions.designPro.app,
  reducer,
  selectors,
};
